import { useEffect, useState } from "react";

import { decodeAccessTokenAndAssignIdentity } from "../utils/Authentication";
import { getCookieBotConsentStatistics } from "../utils/cookieConsent";
import { resetAmplitudeUserIdentity } from "../utils/amplitude";
import { resetUserAttributesInDatadogRUM } from "../utils/datadog";

/**
 * Resolves the global `window` for Cookiebot event listening. Exposed as a
 * mutable config object (rather than an inline expression) purely so tests
 * can simulate a windowless environment by overriding `getWindow`, without
 * deleting jsdom's own `window` and breaking React DOM's rendering internals
 * in the process. Production default is unchanged.
 */
export const cookieConsentEnvironment: { getWindow: () => Window | undefined } =
  {
    getWindow: () => (globalThis as unknown as { window?: Window }).window,
  };

/**
 *
 * @returns cookieBotConsentStatistics - boolean value indicating statistics consent status
 */
export const useCookieConsent = (): { cookieBotConsentStatistics: boolean } => {
  const [cookieBotConsentStatistics, setCookieBotConsentStatistics] =
    useState<boolean>(getCookieBotConsentStatistics());

  useEffect(() => {
    const eventTarget = cookieConsentEnvironment.getWindow();
    if (!eventTarget) {
      return;
    }

    const handleAccept = (): void => {
      const hasStatisticsConsent = getCookieBotConsentStatistics();
      setCookieBotConsentStatistics(hasStatisticsConsent);

      if (hasStatisticsConsent) {
        const existingAccessToken = localStorage.getItem("access_token");
        if (existingAccessToken) {
          try {
            decodeAccessTokenAndAssignIdentity(existingAccessToken);
          } catch (error) {
            console.error(
              "Failed to assign telemetry identity after consent accept:",
              error
            );
          }
        }
      } else {
        resetAmplitudeUserIdentity();
        resetUserAttributesInDatadogRUM();
      }
    };
    const handleDecline = (): void => {
      setCookieBotConsentStatistics(false);
      resetAmplitudeUserIdentity();
      resetUserAttributesInDatadogRUM();
    };

    eventTarget.addEventListener("CookiebotOnAccept", handleAccept);
    eventTarget.addEventListener("CookiebotOnDecline", handleDecline);

    return () => {
      eventTarget.removeEventListener("CookiebotOnAccept", handleAccept);
      eventTarget.removeEventListener("CookiebotOnDecline", handleDecline);
    };
  }, []);

  return { cookieBotConsentStatistics };
};
